import type Point from '@arcgis/core/geometry/Point.js';
import {
  registerHandle,
  resolve,
  summarizeRef,
  type ResolvedRef,
  type Source,
  SourceSchema,
} from '@/agent/refs';
import type { Tool } from './types';

type Input = { source: Source };

export const computeCentroid: Tool<Input> = {
  name: 'compute_centroid',
  description:
    'Return the centroid of a Source as a point. For points the source point itself is returned.',
  input_schema: {
    type: 'object',
    properties: { source: SourceSchema },
    required: ['source'],
    additionalProperties: false,
  },
  execute: async ({ source }) => {
    const ref = await resolve(source);
    if (!ref.geometry && !ref.extent) {
      throw new Error('compute_centroid: source has no geometry or extent');
    }
    let centroid: Point | null = null;
    if (ref.geometry?.type === 'point') {
      centroid = ref.geometry as Point;
    } else if (ref.geometry && 'centroid' in ref.geometry) {
      centroid = (ref.geometry as { centroid?: Point }).centroid ?? null;
    }
    if (!centroid && ref.extent) {
      centroid = ref.extent.center;
    }
    if (!centroid) throw new Error('compute_centroid: could not derive a centroid');

    const newRef: ResolvedRef = {
      source_kind: 'geometry',
      geometry: centroid,
      extent: ref.extent,
    };
    const handle = registerHandle(newRef);
    return {
      handle,
      point: {
        x: centroid.x,
        y: centroid.y,
        wkid: centroid.spatialReference?.wkid ?? undefined,
      },
      summary: summarizeRef(newRef),
    };
  },
};
